import { cn } from "@/lib/utils"

// Shared dining-room occupancy visual used by /dashboard and /tables so both
// screens read as the same product: segmented bar + dot legend, no digit grids.
export type OccupancySummary = {
  total: number
  available: number
  occupied: number
  reserved: number
  disabled: number
}

export function occupancyPercent(summary: OccupancySummary): number {
  const participatingTables = Math.max(summary.total - summary.disabled, 0)
  if (participatingTables === 0) return 0

  const occupiedTables = Math.min(
    Math.max(summary.occupied, 0),
    participatingTables
  )

  return Math.round((occupiedTables / participatingTables) * 100)
}

export function OccupancyBar({ summary }: { summary: OccupancySummary }) {
  if (summary.total === 0) {
    return <div className="h-2 w-full rounded-full bg-muted" />
  }

  const segments = [
    { key: "occupied", count: summary.occupied, className: "bg-status-occupied" },
    { key: "reserved", count: summary.reserved, className: "bg-status-reserved" },
    { key: "available", count: summary.available, className: "bg-status-available" },
    { key: "disabled", count: summary.disabled, className: "bg-status-disabled/40" },
  ].filter((segment) => segment.count > 0)

  return (
    <div className="flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted">
      {segments.map((segment) => (
        <div
          key={segment.key}
          className={segment.className}
          style={{ width: `${(segment.count / summary.total) * 100}%` }}
        />
      ))}
    </div>
  )
}

export function OccupancyLegendItem({
  count,
  label,
  dotClassName,
}: {
  count: number
  label: string
  dotClassName: string
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClassName)} />
      <span className="text-sm font-medium tabular-nums">{count}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}
